/**
 * Shared enum tuples used across multiple schemas.
 *
 * Each export is `as const` so it can be spread into `z.enum([...])`.
 * Schemas pick the full set or a subset depending on their ACT.
 */

import {
	ComparecyclePaymentMethods,
	CorePaymentMethod,
	DeviceTypes,
	EtisalatPaymentMethods,
	ExternalPaymentMethod,
} from "../../enums";

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------

export const LANGUAGES = [
	"English",
	"Greek",
	"Japanese",
	"Australian",
	"Italian",
	"Arabic",
	"French",
	"Dutch",
	"Czech",
	"Russian",
	"Estonian",
	"German",
	"Slovak",
	"Croatian",
	"Portuguese",
] as const;

// ---------------------------------------------------------------------------
// Payment Methods — derived from platform source of truth
// (excludes 'unknown' which is not a configurable option)
// ---------------------------------------------------------------------------

export const PAYMENT_METHODS = [
	CorePaymentMethod,
	...EtisalatPaymentMethods,
	...ComparecyclePaymentMethods,
	ExternalPaymentMethod,
] as const;

// ---------------------------------------------------------------------------
// Legal Documents
// ---------------------------------------------------------------------------

export const LEGAL_DOCS = ["privacy", "terms", "device-ownership", "data-erasure"] as const;

// ---------------------------------------------------------------------------
// Component Version
// ---------------------------------------------------------------------------

export const COMPONENT_VERSIONS = ["2", "3"] as const;

// ---------------------------------------------------------------------------
// Device Types — derived from the Prisma-guarded source of truth
// ---------------------------------------------------------------------------

export const DEVICE_TYPES = DeviceTypes;

// ---------------------------------------------------------------------------
// Functional Assessments
// ---------------------------------------------------------------------------

export const FUNCTIONAL_ASSESSMENTS = [
	"digitizer",
	"audio",
	"multitouch",
	"biometrics",
	"gyroscope",
	"front-camera-ai",
	"back-camera-ai",
	"self-front-cosmetics",
	"self-back-cosmetics",
	"self-body-frame-cosmetics",
	"self-camera-cosmetics",
	"self-lcd-condition",
	"self-buttons-work",
] as const;

// ---------------------------------------------------------------------------
// Cosmetic Components
// ---------------------------------------------------------------------------

export const COSMETIC_COMPONENTS_SMARTPHONE = [
	"self-device-turns-on",
	"self-front-cosmetics",
	"self-back-cosmetics",
	"self-body-frame-cosmetics",
	"self-camera-cosmetics",
	"self-lcd-condition",
] as const;

export const COSMETIC_COMPONENTS_SMARTWATCH = [
	"self-charger",
	"self-device-material",
	"self-overall-condition",
	"self-strap",
] as const;

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

export const FONTS = [
	"",
	"Manrope",
	"Helvetica",
	"peridot-pe-variable",
	"Roboto",
	"AzoSansWeb",
	"Thicker",
	"Iliad",
	"BTCosmoBold",
	"TeleNeo",
] as const;

// ---------------------------------------------------------------------------
// Grading Sets
// ---------------------------------------------------------------------------

export const GRADING_SETS = ["grade", "grade-v2", "grade-v3", "grade-atm", "warehouse", "warehouse-v3"] as const;

// ---------------------------------------------------------------------------
// Capture Angles (Vision)
// ---------------------------------------------------------------------------

export const CAPTURE_ANGLES = [
	"Top",
	"Bottom",
	"Right",
	"Left",
	"Back",
	"Front",
	"FrontWhite",
	"BodyFrameTop",
	"BodyFrameBottom",
	"BodyFrameLeft",
	"BodyFrameRight",
	"BackCameras",
] as const;

// ---------------------------------------------------------------------------
// Quality Check Names (Vision Global)
// ---------------------------------------------------------------------------

export const QUALITY_CHECK_NAMES = [
	"GeneralPlacementError",
	"NotInsideCameraView",
	"NotCloseEnough",
	"NotBackSide",
	"NotFrontSide",
	"NotSteady",
	"Slower",
	"BlurryPhoto",
] as const;

// ---------------------------------------------------------------------------
// Logging Levels
// ---------------------------------------------------------------------------

export const LOG_LEVELS = ["trace", "debug", "info", "warn", "error", "fatal"] as const;

// ---------------------------------------------------------------------------
// Resolver Options (Remote Grader)
// ---------------------------------------------------------------------------

export const RESOLVER_OPTIONS = ["auto-responder", "ai", "human-agent", "ai-passthrough"] as const;
